import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppButton, AppTextField } from "../../../components/form";
import { AuthPageShell } from "../../../components/layout";
import DateRangeHeader, { useDateRangeHeader } from "../../../components/DateRangeHeader";
import DashboardGrid from "../../../components/DashboardGrid";
import { useClientBranding } from "../../../providers/ClientBrandingProvider";
import { useMaterialUIController } from "../../../context";
import { publicShareService } from "../../../services/publicShareService";
import type { DashboardExecuteResponse, DashboardDecorationPlacement, DashboardVisualPlacement, DashboardWidgetPlacement } from "../../../types/dashboard";
import type { PublicShareMeta } from "../../../types/share";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";

function isExpired(iso?: string): boolean {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}

function normalizeExecuteResponse(res: unknown): DashboardExecuteResponse {
  if (!res || typeof res !== "object") {
    return { widgets: [] };
  }
  const data = res as DashboardExecuteResponse;
  const widgets = (data.widgets ?? []).map((w) => ({
    ...w,
    widgetId: String(w.widgetId ?? ""),
  }));
  return { ...data, widgets };
}

function toPlacements(res: DashboardExecuteResponse): DashboardWidgetPlacement[] {
  return (res.widgets ?? []).map((w) => ({
    widgetId: w.widgetId,
    x: w.x,
    y: w.y,
    w: w.w,
    h: w.h,
    titleOverride: w.title,
  }));
}

export default function PublicSharePage() {
  const { token = "" } = useParams();
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const { brandImageLight, brandImageDark, brandName } = useClientBranding();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const brandLogo = darkMode ? brandImageLight : brandImageDark;
  const date = useDateRangeHeader();
  const [meta, setMeta] = useState<PublicShareMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [results, setResults] = useState<DashboardExecuteResponse | undefined>();
  const [placements, setPlacements] = useState<DashboardWidgetPlacement[]>([]);
  const [decorations, setDecorations] = useState<DashboardDecorationPlacement[]>([]);
  const [visuals, setVisuals] = useState<DashboardVisualPlacement[]>([]);
  const [executing, setExecuting] = useState(false);
  const [loadingWidgetIds, setLoadingWidgetIds] = useState<Set<string>>(new Set());
  const [executedOnce, setExecutedOnce] = useState(false);
  const executeRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const placementsRef = useRef(placements);
  placementsRef.current = placements;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const execute = useCallback(async () => {
    setExecuting(true);
    setExecuteError(null);
    if (placementsRef.current.length > 0) {
      setLoadingWidgetIds(new Set(placementsRef.current.map((p) => p.widgetId)));
    }
    try {
      const raw = await publicShareService.execute(token, date.range, password || undefined);
      const res = normalizeExecuteResponse(raw);
      setResults(res);
      setPlacements(toPlacements(res));
      setExecutedOnce(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 410) {
        setError(t("publicShare.expired"));
      } else {
        setExecuteError(t("publicShare.executeFailed"));
        console.error("Public share execute failed:", err);
      }
    } finally {
      setExecuting(false);
      setLoadingWidgetIds(new Set());
    }
  }, [token, date.range.from, date.range.to, password, t]);

  executeRef.current = execute;

  useEffect(() => {
    setMetaLoading(true);
    publicShareService
      .getMeta(token)
      .then((m) => {
        if (isExpired(m.expiresAt)) {
          setError(t("publicShare.expired"));
          return;
        }
        setMeta(m);
        setDecorations(m.decorations || []);
        setVisuals(m.visuals || []);
        if (!m.needsPassword) setAuthed(true);
      })
      .catch((err) => {
        if (axios.isAxiosError(err) && err.response?.status === 410) {
          setError(t("publicShare.expired"));
        } else {
          setError(t("publicShare.notFound"));
        }
      })
      .finally(() => setMetaLoading(false));
  }, [token, t]);

  useEffect(() => {
    if (!authed || !meta) return;
    if (meta.runtimeMode === "SNAPSHOT") {
      executeRef.current?.();
    }
  }, [authed, meta?.runtimeMode, meta?.snapshotFrom, meta?.snapshotTo]);

  useEffect(() => {
    if (!authed || meta?.runtimeMode !== "LIVE") return;
    const timer = window.setTimeout(() => {
      executeRef.current?.();
    }, 450);
    return () => window.clearTimeout(timer);
  }, [authed, meta?.runtimeMode, date.range.from, date.range.to]);

  const formatSnapshotDate = (iso?: string) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const onAuth = async () => {
    try {
      await publicShareService.auth(token, password);
      setAuthed(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 410) {
        setError(t("publicShare.expired"));
      }
    }
  };

  if (metaLoading && !error) {
    return (
      <AuthPageShell title={t("publicShare.title")} subtitle={t("publicShare.loading")}>
        <Stack spacing={2} alignItems="center" py={2}>
          <CircularProgress />
        </Stack>
      </AuthPageShell>
    );
  }

  if (error) {
    return (
      <AuthPageShell title={t("publicShare.title")}>
        <Alert severity="error">{error}</Alert>
      </AuthPageShell>
    );
  }

  if (meta?.needsPassword && !authed) {
    return (
      <AuthPageShell
        brand={
          <Box
            component="img"
            src={brandLogo}
            alt={brandName}
            sx={custom.pages.shared.brandLogoHeight}
          />
        }
        title={meta?.dashboardName || t("publicShare.title")}
        subtitle={t("share.enterPassword")}
      >
        <Stack spacing={2}>
          <AppTextField
            fullWidth
            type="password"
            label={t("share.enterPassword")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <AppButton variant="contained" fullWidth onClick={onAuth}>
            {t("share.auth")}
          </AppButton>
        </Stack>
      </AuthPageShell>
    );
  }

  return (
    <Box sx={custom.pages.publicShare.page}>
      <Box sx={custom.pages.publicShare.sidebar}>
        <Stack spacing={2} sx={custom.pages.shared.sidebarContentStack}>
          <Stack spacing={1.5} alignItems={{ xs: "center", md: "flex-start" }}>
            <Box
              component="img"
              src={brandLogo}
              alt={brandName}
              sx={custom.pages.shared.brandLogoContained}
            />
            <Typography
              variant="h6"
              fontWeight={700}
              sx={custom.pages.shared.responsiveTitleAlign}
            >
              {meta?.dashboardName || t("publicShare.title")}
            </Typography>
          </Stack>

          {authed && meta?.runtimeMode === "LIVE" && (
            <DateRangeHeader
              layout="vertical"
              showYearMonthChips
              selectedYear={date.selectedYear}
              availableYears={date.availableYears}
              onYearSelect={date.applyYear}
              yearMonthChips={date.yearMonthChips}
              activeYearMonth={date.activeYearMonth}
              onYearMonthSelect={date.applyYearMonth}
              range={date.range}
              preset={date.preset}
              onPresetChange={date.setPreset}
              customFrom={date.customFrom}
              customTo={date.customTo}
              onCustomFromChange={date.setCustomFrom}
              onCustomToChange={date.setCustomTo}
              onApplyCustom={date.applyCustom}
              label={date.label}
              onRefresh={execute}
              executing={executing}
            />
          )}

          {authed && meta?.runtimeMode === "SNAPSHOT" && meta.snapshotFrom && meta.snapshotTo && (
            <Alert severity="info" sx={custom.pages.shared.alertCompactPy}>
              {t("publicShare.snapshotRange", {
                from: formatSnapshotDate(meta.snapshotFrom),
                to: formatSnapshotDate(meta.snapshotTo),
              })}
              <Typography variant="caption" component="div" sx={custom.pages.shared.captionMarginTopHalf}>
                {t("publicShare.snapshotNote")}
              </Typography>
            </Alert>
          )}

          {executeError && (
            <Alert severity="error" sx={custom.pages.shared.alertCompactPy}>
              {executeError}
            </Alert>
          )}

        </Stack>
      </Box>

      <Box sx={custom.pages.publicShare.mainContent}>
        {authed && (placements.length > 0 || decorations.length > 0 || visuals.length > 0) && (
          <DashboardGrid
            placements={placements}
            decorations={decorations}
            visuals={visuals}
            results={results}
            dataQueries={results?.dataQueries}
            editable={false}
            rowHeight={meta?.rowHeight && meta.rowHeight > 0 ? meta.rowHeight : 80}
            loadingWidgetIds={loadingWidgetIds}
            globalLoading={executing}
          />
        )}

        {authed && executing && placements.length === 0 && decorations.length === 0 && visuals.length === 0 && (
          <Box sx={custom.pages.shared.centeredLoadingBox}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
}
